import React from 'react';
import { useParams } from 'react-router-dom';
import { capabilities, cta, industries, parts } from '../content';
import { Arrow, Button, Phone, SmartLink, StatusChip, Swipe, usePageMeta } from '../ui';
import { ClaimExplainer, FormsExplainer, HandoverExplainer, OwnerScanExplainer, PartsExplainer, TagExplainer, TransferExplainer, type ExplainerProps } from '../explainers';
import { heroUnits } from '../heroUnits';
import { CtaBand, Faq, KitStyles, PageHero, RowLink, Section, SwipeLine, TwoTone, breadcrumbJsonLd, useJsonLd } from './platform/kit';
import { BrandConsole, PartsScreen, PassportScreen, SealedForm, UnitLife } from './platform/mockups';
import { UnitPlateHero } from './platform/UnitPlate';
import { Device, HandoverScreen } from '../phone';
import { sectorByKey } from '../variants/c/sectors';
import { sectorPages, sectorUnits } from './industries/data';
import { DETAIL_ORDER, DETAILS, DPP_AHEAD, DPP_DECIDED, DPP_LAST_VERIFIED, DPP_UNDECIDED, type Detail, type DetailSlug, type ExplainerKey } from './platform/data';

const EXPLAINERS: Record<ExplainerKey, React.ComponentType<ExplainerProps>> = {
  owner: OwnerScanExplainer,
  handover: HandoverExplainer,
  claim: ClaimExplainer,
  transfer: TransferExplainer,
  tag: TagExplainer,
  forms: FormsExplainer,
  parts: PartsExplainer,
};

function HeroVisual({ slug }: { slug: DetailSlug }) {
  switch (slug) {
    case 'registration-warranty':
      return <UnitPlateHero unit="mower" />;
    case 'dealers':
      return (
        <Device s={sectorByKey('mower')} label="A dealer's pre-delivery sign-off for a Moorcroft ride-on mower">
          <HandoverScreen s={sectorByKey('mower')} />
        </Device>
      );
    case 'service':
      return <BrandConsole compact />;
    case 'ownership':
      return <UnitLife />;
    case 'digital-product-passport':
      return (
        <Phone>
          <PassportScreen />
        </Phone>
      );
    case 'parts':
      return (
        <Phone>
          <PartsScreen unit={heroUnits[0]} part={{ part: parts.sample.part, price: parts.sample.price, by: parts.sample.by }} />
        </Phone>
      );
    case 'forms':
      return <SealedForm />;
  }
}

function Pains({ d }: { d: Detail }) {
  return (
    <>
      <div className="max-w-3xl" data-reveal>
        <TwoTone lead={d.pains.title[0]} rest={d.pains.title[1]} />
      </div>
      <div className="mt-12 grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-16">
        <div className="frame" data-reveal>
          <img src={d.photo.src} alt={d.photo.alt} width={1536} height={1024} loading="lazy" className="aspect-[3/2] w-full object-cover" />
        </div>
        <div>
          <ul className="border-t border-bm-line">
            {d.pains.quotes.map((q, i) => (
              <li key={q} className="grid grid-cols-[28px_1fr] gap-3 border-b border-bm-line py-5" data-reveal style={{ ['--reveal-delay' as string]: `${i * 0.05}s` }}>
                <span aria-hidden="true" className="font-display text-[40px] font-extrabold leading-[0.8] text-bm-green">
                  “
                </span>
                <p className="pk-quote text-[clamp(18px,1.6vw,21px)] leading-snug">{q}</p>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[13.5px] text-bm-muted" data-reveal>
            What we hear from manufacturers.
          </p>
        </div>
      </div>
    </>
  );
}

// A sector unit where one tells the story better than the default lead unit.
const UNITS: Partial<Record<ExplainerKey, ExplainerProps['unit']>> = {
  transfer: sectorUnits['premium-durables'],
};

function HowItWorks({ d }: { d: Detail }) {
  const Explainer = EXPLAINERS[d.explainer];
  return (
    <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
      <div>
        <div data-reveal>
          <TwoTone lead={d.how.title[0]} rest={d.how.title[1]} />
        </div>
        <ol className="mt-10 border-t border-bm-line">
          {d.how.steps.map((s, i) => (
            <li key={s.t} className="grid grid-cols-[40px_1fr] gap-x-3 border-b border-bm-line py-5" data-reveal style={{ ['--reveal-delay' as string]: `${i * 0.05}s` }}>
              <span className="font-mono text-[13px] leading-[1.9] text-bm-emerald">0{i + 1}</span>
              <div>
                <h3 className="text-[18px] font-bold tracking-[-0.015em] text-bm-indigo">{s.t}</h3>
                <p className="mt-1.5 text-[15.5px] text-bm-muted">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className="lg:sticky lg:top-28 lg:self-start" data-reveal>
        <Explainer unit={UNITS[d.explainer]} />
      </div>
    </div>
  );
}

function CapabilityLedger({ keys }: { keys: string[] }) {
  return (
    <div className="border-t border-bm-line">
      {keys.map((k) => {
        const c = capabilities.find((x) => x.key === k);
        if (!c) return null;
        return (
          <div key={k} className="grid gap-2 border-b border-bm-line py-5 md:grid-cols-[240px_1fr_auto] md:items-baseline md:gap-8">
            <div className="text-[17px] font-semibold text-bm-indigo">{c.title}</div>
            <div className="text-[15.5px] text-bm-ink">{c.line}</div>
            <div>
              <StatusChip status={c.status} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- area-specific sections ---------- */

function DppStatus() {
  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
        <div data-reveal>
          <TwoTone lead="What is decided, and what is not." />
        </div>
        <div className="lg:justify-self-end" data-reveal>
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[14px] text-bm-indigo shadow-[0_0_0_1px_#E4E3F0]">
            <span className="h-2 w-2 rounded-full bg-bm-green" aria-hidden="true" />
            Last verified {DPP_LAST_VERIFIED}
          </div>
        </div>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <div className="pk-card p-6 sm:p-8" data-reveal>
          <h3 className="flex items-center gap-3 text-[20px] font-bold text-bm-indigo">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-bm-indigo text-[13px] text-white" aria-hidden="true">
              ✓
            </span>
            Decided
          </h3>
          <ul className="mt-6 border-t border-bm-line">
            {DPP_DECIDED.map((x) => (
              <li key={x.t} className="border-b border-bm-line py-5 last:border-b-0 last:pb-0">
                <div className="text-[16.5px] font-semibold text-bm-indigo">{x.t}</div>
                <p className="mt-1.5 text-[15px] text-bm-muted">{x.d}</p>
                <div className="mt-2 font-mono text-[11.5px] text-bm-emerald">{x.src}</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-[20px] border border-dashed border-[#C9C7DD] p-6 sm:p-8" data-reveal style={{ ['--reveal-delay' as string]: '0.08s' }}>
          <h3 className="flex items-center gap-3 text-[20px] font-bold text-bm-indigo">
            <span className="grid h-7 w-7 place-items-center rounded-full text-[15px] text-bm-indigo shadow-[inset_0_0_0_1.5px_#241E61]" aria-hidden="true">
              ?
            </span>
            Not decided yet
          </h3>
          <ul className="mt-6 border-t border-bm-line">
            {DPP_UNDECIDED.map((x) => (
              <li key={x.t} className="border-b border-bm-line py-5 last:border-b-0 last:pb-0">
                <div className="text-[16.5px] font-semibold text-bm-indigo">{x.t}</div>
                <p className="mt-1.5 text-[15px] text-bm-muted">{x.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function AiSupport() {
  return (
    <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-16">
      <div data-reveal>
        <h2 className="t-h2">
          Answers from your <Swipe>manuals.</Swipe>
        </h2>
        <p className="t-lede mt-6 max-w-xl">
          Each answer uses that unit&rsquo;s records and shows the page it came from.
        </p>
        <ul className="mt-8 border-t border-bm-line">
          {[
            ['Grounded', 'From your documents for that model and build.'],
            ['Handed over', 'Your team sees every conversation and can take over.'],
            ['Capped', 'A spending cap on each AI meter. AI pauses when it is reached.'],
            ['Private', 'No shared model is trained on your data.'],
          ].map(([t, d]) => (
            <li key={t} className="grid gap-1 border-b border-bm-line py-4 sm:grid-cols-[130px_1fr] sm:gap-6">
              <span className="text-[15.5px] font-semibold text-bm-indigo">{t}</span>
              <span className="text-[15.5px] text-bm-muted">{d}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <SmartLink href="/platform/ai" className="pk-textlink">
            How the product expert works <Arrow />
          </SmartLink>
        </div>
      </div>
      <div className="pd-mock" data-reveal style={{ ['--reveal-delay' as string]: '0.1s' }}>
        <Phone>
          <div className="flex h-full flex-col bg-[#F7F5F0] pt-9 text-left text-[#2D2D2D]" style={{ fontSize: 12 }}>
            <div className="flex items-center justify-between px-4 pb-3">
              <span style={{ fontWeight: 800, letterSpacing: '0.18em', fontSize: 12 }}>MONVERTA</span>
              <span className="font-mono" style={{ fontSize: 9.5, opacity: 0.6 }}>
                CLA9-2024-00091
              </span>
            </div>
            <div className="flex-1 space-y-2.5 px-3">
              <div className="ml-auto max-w-[78%] rounded-2xl rounded-br-md bg-[#2D2D2D] px-3 py-2 text-white" style={{ fontSize: 11.5 }}>
                It keeps cutting out after a minute
              </div>
              <div className="max-w-[86%] rounded-2xl rounded-bl-md bg-white px-3 py-2.5" style={{ fontSize: 11.5 }}>
                On the A9 that usually means the filter is blocked. Take out the filter (step 4 on page 12), tap it clean and let it dry for 24 hours.
                <div className="mt-2 inline-block rounded-md bg-[#F7F5F0] px-2 py-1" style={{ fontSize: 9.5, color: '#2E4A3E' }}>
                  Source: A9 user manual, 2024 build, p.12
                </div>
              </div>
              <div className="ml-auto max-w-[78%] rounded-2xl rounded-br-md bg-[#2D2D2D] px-3 py-2 text-white" style={{ fontSize: 11.5 }}>
                Done that, still the same
              </div>
              <div className="max-w-[86%] rounded-2xl rounded-bl-md bg-white px-3 py-2.5" style={{ fontSize: 11.5 }}>
                Thanks for trying that. Your A9 is in warranty, so I have opened a case for our service team with your serial and what you have tried.
              </div>
            </div>
            <div className="m-3 rounded-xl px-3 py-2.5" style={{ background: '#2E4A3E', color: '#fff' }}>
              <div style={{ fontWeight: 600, fontSize: 11.5 }}>Case 4127 opened</div>
              <div style={{ fontSize: 9.5, opacity: 0.75 }}>A person will reply today</div>
            </div>
          </div>
        </Phone>
      </div>
    </div>
  );
}

function DealerPricing() {
  return (
    <div className="grid gap-8 rounded-[22px] bg-white p-6 shadow-[0_0_0_1px_#E4E3F0] sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center" data-reveal>
      <div>
        <h2 className="t-h3 max-w-2xl">Priced by branch, not by user.</h2>
        <p className="mt-3 max-w-2xl text-[16px] text-bm-muted">
          The Partner (Dealer) add-on is £9,000 (€10,800) a year for up to 100 branches, plus £2,500 (€3,000) for each further 50. It sits on the Scale plan.
        </p>
      </div>
      <SmartLink href="/pricing" className="pk-textlink">
        See pricing <Arrow />
      </SmartLink>
    </div>
  );
}


/* ---------- forms and parts ---------- */

const SECTOR_ORDER = ['heating-heat-pumps', 'agricultural-machinery', 'lifting-access-equipment', 'commercial-kitchen', 'industrial-precision', 'premium-durables', 'medical-laboratory-instruments', 'sports-fitness-equipment'];

function FormsBySector() {
  const rows = SECTOR_ORDER.map((slug) => ({ slug, ind: industries.find((i) => i.slug === slug), f: sectorPages[slug]?.formExample })).filter((r) => r.ind && r.f);
  return (
    <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
      <div data-reveal>
        <TwoTone lead="The forms your trade already signs." />
        <p className="mt-5 max-w-sm text-[16px] text-bm-muted">Start with one form and one dealer group.</p>
      </div>
      <div className="border-t border-bm-line" data-reveal>
        {rows.map(({ slug, ind, f }) => (
          <SmartLink key={slug} href={`/industries/${slug}`} className="group grid gap-1 border-b border-bm-line py-4 sm:grid-cols-[1fr_1.1fr_auto] sm:items-baseline sm:gap-6">
            <span className="text-[17px] font-semibold text-bm-indigo">{f!.t}</span>
            <span className="text-[15px] text-bm-muted">
              {f!.who}
              <span className="block text-[13px]">{ind!.name}</span>
            </span>
            <span className="hidden text-bm-muted transition-colors group-hover:text-bm-indigo sm:block" aria-hidden="true">
              <Arrow />
            </span>
          </SmartLink>
        ))}
      </div>
    </div>
  );
}

function SignatureFacts() {
  return (
    <div className="grid gap-8 rounded-[22px] bg-white p-6 shadow-[0_0_0_1px_#E4E3F0] sm:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14" data-reveal>
      <div>
        <h2 className="t-h3 max-w-md">A simple e-signature, fully audited.</h2>
        <p className="mt-4 max-w-md text-[16px] text-bm-muted">Documents that need a deed, a witness or a qualified signature can stay on paper.</p>
        <div className="mt-6">
          <SmartLink href="/legal/terms/#10b-electronic-signatures-signed-forms-and-safety-critical-work" className="pk-textlink">
            Read clause 10B of our Customer Terms <Arrow />
          </SmartLink>
        </div>
      </div>
      <ul className="border-t border-bm-line">
        {[
          ['Sealed', 'Each form is sealed as a PDF with an audit page.'],
          ['Recorded', 'Names, time, IP address, user agent, consent text and a document hash.'],
          ['Verifiable', 'A verification link on every sealed form.'],
          ['Your responsibility', 'Whoever does safety-critical work answers for being qualified, and for what they record.'],
        ].map(([t, d]) => (
          <li key={t} className="grid gap-1 border-b border-bm-line py-4 sm:grid-cols-[150px_1fr] sm:gap-6">
            <span className="text-[15.5px] font-semibold text-bm-indigo">{t}</span>
            <span className="text-[15.5px] text-bm-muted">{d}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function YouSell() {
  return (
    <div className="grid gap-8 rounded-[22px] bg-white p-6 shadow-[0_0_0_1px_#E4E3F0] sm:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14" data-reveal>
      <div>
        <h2 className="t-h3 max-w-md">You stay the seller.</h2>
        <p className="mt-4 max-w-md text-[16px] text-bm-muted">We provide the software, and never buy, sell or take title to the goods.</p>
        <div className="mt-6">
          <SmartLink href={cta.priceListHref} className="pk-textlink">
            {cta.priceList} <Arrow />
          </SmartLink>
        </div>
      </div>
      <ul className="border-t border-bm-line">
        {[
          ['Merchant of record', 'You, even when a dealer fits or collects the order.'],
          ['Payments', 'Into your own payment account.'],
          ['Platform fee', '2% of each payment plus £0.25. Provider fees are separate.'],
          ['Your terms', 'Your prices, returns and refunds.'],
        ].map(([t, d]) => (
          <li key={t} className="grid gap-1 border-b border-bm-line py-4 sm:grid-cols-[170px_1fr] sm:gap-6">
            <span className="text-[15.5px] font-semibold text-bm-indigo">{t}</span>
            <span className="text-[15.5px] text-bm-muted">{d}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- not found ---------- */

function UnknownArea() {
  usePageMeta('Platform page not found', 'That platform page does not exist. Find registration, dealers, service, ownership transfer and Digital Product Passport pages here.');
  return (
    <>
      <KitStyles />
      <section className="section">
        <div className="wrap max-w-4xl">
          <div className="t-eyebrow">Not found</div>
          <h1 className="pk-h1 mt-4">We could not find that part of the platform.</h1>
          <p className="t-lede mt-5 max-w-xl">It may have moved. These are the areas we cover:</p>
          <div className="mt-10 border-t border-bm-line">
            {DETAIL_ORDER.map((slug) => (
              <RowLink key={slug} href={`/platform/${slug}`} title={DETAILS[slug].navTitle} line={DETAILS[slug].navLine} status={DETAILS[slug].status} />
            ))}
            <RowLink href="/integrations" title="ERP integrations" line="Epicor Kinetic today. IFS and SAP through our API." />
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button href={cta.primaryHref}>
              {cta.primary} <Arrow />
            </Button>
            <SmartLink href="/platform" className="pk-textlink text-[15px] sm:ml-2">
              or see the platform overview
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}

/* ---------- one next step, not a second menu ---------- */

function NextUp({ slug }: { slug: DetailSlug }) {
  const i = DETAIL_ORDER.indexOf(slug);
  const next = DETAIL_ORDER[(i + 1) % DETAIL_ORDER.length];
  const n = DETAILS[next];
  return (
    <div className="mt-20 flex flex-wrap items-center justify-between gap-x-10 gap-y-3 border-y border-bm-line py-6" data-reveal>
      <SmartLink href={`/platform/${next}`} className="group flex min-w-0 items-baseline gap-4">
        <span className="text-[14.5px] font-semibold text-bm-muted">Next</span>
        <span className="text-[clamp(20px,2.2vw,28px)] font-bold tracking-[-0.025em] text-bm-indigo group-hover:text-bm-emerald">
          {n.navTitle}
          <span className="ml-2 inline-block align-middle">
            <Arrow />
          </span>
        </span>
      </SmartLink>
      <SmartLink href="/platform" className="pk-textlink text-[15px]">
        All of the platform
      </SmartLink>
    </div>
  );
}

/* ---------- page ---------- */

function DppAhead() {
  return (
    <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
      <div data-reveal>
        <TwoTone lead="Why start before your date." />
        <p className="t-lede mt-5 max-w-[26rem]">No date for your group is no reason to wait. The units you ship this year will still be in use when it arrives.</p>
      </div>
      <ol className="border-t border-bm-line" data-reveal>
        {DPP_AHEAD.map((x, i) => (
          <li key={x.t} className="flex gap-5 border-b border-bm-line py-6">
            <span className="mt-[3px] font-mono text-[12.5px] text-bm-emerald">0{i + 1}</span>
            <div>
              <div className="text-[17px] font-semibold text-bm-indigo">{x.t}</div>
              <p className="mt-1.5 text-[15px] leading-relaxed text-bm-muted">{x.d}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DetailPage({ d }: { d: Detail }) {
  const slug = d.slug as DetailSlug;
  usePageMeta(d.metaTitle, d.metaDescription);
  useJsonLd(
    `bc-${slug}`,
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Platform', path: '/platform' },
      { name: d.navTitle, path: `/platform/${slug}` },
    ])
  );

  const extras: [string, React.ReactNode][] =
    slug === 'digital-product-passport'
      ? [
          ['decided', <DppStatus />],
          ['ahead', <DppAhead />],
        ]
      : slug === 'service'
        ? [['ai', <AiSupport />]]
        : slug === 'dealers'
          ? [['pricing', <DealerPricing />]]
          : slug === 'forms'
            ? [
                ['sectors', <FormsBySector />],
                ['signature', <SignatureFacts />],
              ]
            : slug === 'parts'
              ? [['seller', <YouSell />]]
              : [];
  const sections: [string, React.ReactNode][] = [
    ['pains', <Pains d={d} />],
    ['how', <HowItWorks d={d} />],
    ...extras,
    [
      'included',
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <div data-reveal>
          <h2 className="t-h2">What is included.</h2>
        </div>
        <div data-reveal>
          <CapabilityLedger keys={d.capabilities} />
        </div>
      </div>,
    ],
    [
      'faq',
      <>
        <Faq faqs={d.faqs} id={`faq-${slug}`} />
        <NextUp slug={slug} />
      </>,
    ],
  ];

  return (
    <div className="pd">
      <KitStyles />
      <PageHero
        crumbs={[
          { label: 'Platform', href: '/platform' },
          { label: d.navTitle, href: `/platform/${slug}` },
        ]}
        kicker={d.kicker}
        title={<SwipeLine parts={d.h1} draw />}
        lede={d.lede}
        status={d.status}
        aside={
          <div className="pd-mock flex justify-center lg:justify-end">
            <div className={slug === 'service' || slug === 'ownership' || slug === 'forms' || slug === 'registration-warranty' ? 'w-full max-w-[580px]' : ''}>
              <HeroVisual slug={slug} />
            </div>
          </div>
        }
      />
      <div className="wrap pb-4 lg:-mt-6">
        <p className="flex flex-col gap-y-1 border-y border-bm-line py-3.5 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-3 text-[14.5px] leading-snug text-bm-muted">
          {d.facts.map(([k, v], i) => (
            <React.Fragment key={k}>
              {i > 0 && (
                <span aria-hidden="true" className="hidden text-bm-line sm:inline">
                  ·
                </span>
              )}
              <span>
                {k}: <b className="font-semibold text-bm-indigo">{/^[A-Z][a-z]/.test(v) ? v.charAt(0).toLowerCase() + v.slice(1) : v}</b>
              </span>
            </React.Fragment>
          ))}
        </p>
      </div>

      {sections.map(([key, node], i) => (
        <Section key={key} id={key === 'how' || key === 'decided' || key === 'ai' ? key : undefined} alt={i % 2 === 0}>
          {node}
        </Section>
      ))}

      <CtaBand source={`platform-${slug}`} />
    </div>
  );
}

export default function PlatformDetail() {
  const { slug = '' } = useParams();
  const d = (DETAILS as Record<string, Detail>)[slug];
  if (!d) return <UnknownArea />;
  return <DetailPage key={slug} d={d} />;
}
